/* Copyright (c) 2020-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import { useState, useEffect } from "react";
import { useMount } from "react-use";
import { Input } from "@nordcloud/gnui";
import { useDisclosure, useApplicationsPagination } from "~/hooks";
import { isNotEmpty } from "~/tools";
import { useAppEnvFilterSelector } from "../hooks";
import { AppEnvFilteringSidebar } from "./AppEnvFilteringSidebar";
import { ApplicationOption, EnvironmentOption } from "./types";
import {
  getInputLabel,
  generateSelectedEnvs,
  generateSelectedApps,
} from "./utils";

type Props = {
  onClear?: () => void;
  onChange: (apps: string[], envs: string[]) => void;
  selectedApplications: string[];
  selectedEnvironments: string[];
};

export function AppEnvSelectorPaginated({
  onChange,
  onClear,
  selectedApplications,
  selectedEnvironments,
}: Props) {
  const { data, loading, page, setPage, setApplicationName } =
    useApplicationsPagination();

  const [selectedApps, setSelectedApps] = useState<ApplicationOption[]>([]);
  const [selectedEnvs, setSelectedEnvs] = useState<EnvironmentOption[]>([]);

  const { selectApp, selectEnv } = useAppEnvFilterSelector({
    apps: data?.applicationsPaginated?.results,
    selectedApps,
    setSelectedApps,
    selectedEnvs,
    setSelectedEnvs,
  });

  const { isOpen, close, open } = useDisclosure();

  useMount(() => {
    setSelectedApps(generateSelectedApps(selectedApplications, data));
    setSelectedEnvs(generateSelectedEnvs(selectedEnvironments));
  });

  useEffect(() => {
    if (data) {
      setSelectedEnvs((prevState) =>
        generateSelectedEnvs(prevState.map((e) => e.nid))
      );
    }
  }, [isOpen, data]);

  const handleApply = () => {
    onChange(
      selectedApps.map((app) => app.nid),
      selectedEnvs.map((env) => env.nid)
    );
    handleClose();
  };

  const handleClose = () => {
    setPage(0);
    close();
  };

  const onClearHandler = () => {
    setSelectedEnvs([]);
    onClear?.();
  };

  const innerValue = getInputLabel(selectedApps, selectedEnvs);

  return (
    <>
      <Input
        readOnly
        type="text"
        title="Select applications/environments"
        placeholder="Select Applications and Environments"
        value={innerValue}
        icon="sidebar"
        showClearButton={isNotEmpty([...selectedEnvs])}
        onClick={open}
        onClear={onClearHandler}
      />
      <AppEnvFilteringSidebar
        isOpen={isOpen}
        data={data}
        loading={loading}
        page={page}
        setPage={setPage}
        setApplicationName={setApplicationName}
        toggleApp={selectApp}
        toggleEnv={selectEnv}
        selectedApps={selectedApps}
        selectedEnvs={selectedEnvs}
        onClose={handleClose}
        onApply={handleApply}
      />
    </>
  );
}
